import { diffLines } from "diff";
import type { LanguageMode } from "../doc/languageMode.js";
import { highlightCodeClassFor, renderHighlightedFragment } from "../editor/syntaxHighlight.js";
import type { DiffEditorSnapshot } from "./diffTabMonaco.js";

type RowKind = "equal" | "delete" | "insert" | "pad";

export type SplitDiffRow = {
  left: string;
  right: string;
  leftKind: RowKind;
  rightKind: RowKind;
};

function normalizeNewlines(s: string): string {
  return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/** Turn a diff chunk `value` into logical lines (no trailing empty from final newline). */
function linesFromChunk(value: string): string[] {
  if (value.length === 0) return [];
  const parts = value.split("\n");
  if (parts.length > 1 && parts[parts.length - 1] === "") {
    parts.pop();
  }
  return parts;
}

/** Build row-aligned left/right lines for a scroll-locked two-pane view. */
export function buildSplitDiffRows(leftText: string, rightText: string): SplitDiffRow[] {
  const leftN = normalizeNewlines(leftText);
  const rightN = normalizeNewlines(rightText);
  const parts = diffLines(leftN, rightN);
  const rows: SplitDiffRow[] = [];

  let i = 0;
  while (i < parts.length) {
    const part = parts[i]!;

    // Adjacent removed+added: interleave into aligned rows so changed lines
    // appear side-by-side instead of stacked (removed block then added block).
    if (part.removed && i + 1 < parts.length && parts[i + 1]!.added) {
      const delLines = linesFromChunk(part.value);
      const addLines = linesFromChunk(parts[i + 1]!.value);
      const maxLen = Math.max(delLines.length, addLines.length);
      for (let j = 0; j < maxLen; j++) {
        rows.push({
          left: delLines[j] ?? "",
          right: addLines[j] ?? "",
          leftKind: j < delLines.length ? "delete" : "pad",
          rightKind: j < addLines.length ? "insert" : "pad",
        });
      }
      i += 2;
      continue;
    }

    const ls = linesFromChunk(part.value);
    if (part.added) {
      for (const line of ls) {
        rows.push({ left: "", right: line, leftKind: "pad", rightKind: "insert" });
      }
    } else if (part.removed) {
      for (const line of ls) {
        rows.push({ left: line, right: "", leftKind: "delete", rightKind: "pad" });
      }
    } else {
      for (const line of ls) {
        rows.push({ left: line, right: line, leftKind: "equal", rightKind: "equal" });
      }
    }
    i++;
  }

  if (rows.length === 0) {
    rows.push({ left: "", right: "", leftKind: "equal", rightKind: "equal" });
  }
  return rows;
}

function rowClass(kind: RowKind, side: "left" | "right"): string {
  if (kind === "equal") return "";
  if (kind === "pad") return `npp-diff-split-line--pad npp-diff-split-line--pad-${side}`;
  if (kind === "delete") return "npp-diff-split-line--removed";
  return "npp-diff-split-line--added";
}

function cellInnerHtml(text: string, lang: LanguageMode, isPad: boolean): string {
  if (isPad) return "&nbsp;";
  if (text.length === 0) return "&nbsp;";
  return renderHighlightedFragment(text, lang);
}

/** Two-pane compare: one scroll region, aligned rows, syntax highlighting, change backgrounds. */
export function mountDiffSplitPanes(container: HTMLElement, snapshot: DiffEditorSnapshot): () => void {
  container.replaceChildren();
  const root = document.createElement("div");
  root.className = "npp-diff-split-root";

  const head = document.createElement("div");
  head.className = "npp-diff-split-headers";
  const headL = document.createElement("div");
  headL.className = "npp-diff-split-head npp-diff-split-head-left";
  headL.textContent = snapshot.leftLabel;
  const headR = document.createElement("div");
  headR.className = "npp-diff-split-head npp-diff-split-head-right";
  headR.textContent = snapshot.rightLabel;
  head.append(headL, headR);

  const scroll = document.createElement("div");
  scroll.className = "npp-diff-split-scroll";
  const body = document.createElement("div");
  body.className = "npp-diff-split-body";

  const lang = snapshot.highlightLanguage;
  const hlClass = highlightCodeClassFor(lang);
  const rows = buildSplitDiffRows(snapshot.leftText, snapshot.rightText);

  for (const row of rows) {
    const rowEl = document.createElement("div");
    rowEl.className = "npp-diff-split-row";

    const cellL = document.createElement("div");
    cellL.className = `npp-diff-split-cell npp-diff-split-cell-left ${rowClass(row.leftKind, "left")}`.trim();
    const codeL = document.createElement("code");
    codeL.className = hlClass;
    codeL.innerHTML = cellInnerHtml(row.left, lang, row.leftKind === "pad");
    cellL.appendChild(codeL);

    const cellR = document.createElement("div");
    cellR.className = `npp-diff-split-cell npp-diff-split-cell-right ${rowClass(row.rightKind, "right")}`.trim();
    const codeR = document.createElement("code");
    codeR.className = hlClass;
    codeR.innerHTML = cellInnerHtml(row.right, lang, row.rightKind === "pad");
    cellR.appendChild(codeR);

    rowEl.append(cellL, cellR);
    body.appendChild(rowEl);
  }

  scroll.appendChild(body);
  root.append(head, scroll);
  container.appendChild(root);

  return () => {
    container.replaceChildren();
  };
}
